/**
 * Each selected model declares its own headline outputs (Flip: profit and
 * margin on GDV; Buy to let: net annual profit and cash on cash; BRR: cash
 * left in …) through the cards response. The card ledger, the table columns
 * and the lot glance read those declarations instead of assuming "profit".
 */
import type { CardDealResult, PresentationOutput, SelectedDealModel } from '../../api/types'
import { humanize, percent, poundsExact } from '../../api/format'
import { parseStaleReasons } from '../../api/financial'

export type Band = 'good' | 'avg' | 'poor' | 'kill' | 'na'
export const BAND: Record<string, { label: string; cls: Band }> = {
  works: { label: 'Good', cls: 'good' }, promising: { label: 'Average', cls: 'avg' }, marginal: { label: 'Poor', cls: 'poor' }, reject_economics: { label: 'Kill', cls: 'kill' },
  good: { label: 'Good', cls: 'good' }, average: { label: 'Average', cls: 'avg' }, poor: { label: 'Poor', cls: 'poor' }, kill: { label: 'Kill', cls: 'kill' },
}

export function verdictOf(result: CardDealResult | undefined): { label: string; cls: Band } | null {
  if (!result || result.state !== 'computed' || (result.bid && (result.bid.state !== 'computed' || result.bid.maxBidPence == null))) return null
  return BAND[result.verdictBand ?? ''] ?? { label: 'Computed', cls: 'na' }
}

export function modelStatus(result: CardDealResult | undefined): string {
  if (!result) return 'No result yet'
  if (result.state === 'evaluationFailed') return 'Calculation failed'
  if (result.bid?.state === 'noPassingBid') return 'No passing bid'
  if (result.bid && result.bid.state !== 'computed') {
    const labels: Record<string, string> = { missingInputs: 'Missing inputs', notApplicable: 'Not applicable', searchLimitReached: 'Search limit reached', monotonicityViolation: 'Cannot establish a maximum' }
    return labels[result.bid.state] ?? 'Maximum bid unavailable'
  }
  if (result.missingRequiredInput || result.state === 'notComputable') return 'Missing inputs'
  return 'No result'
}

/** The model's headline outputs in declared order, the verdict excluded. */
export function headlineOutputs(model: SelectedDealModel | undefined): PresentationOutput[] {
  const presentation = model?.current?.presentation
  if (!presentation) return []
  const byId = new Map(presentation.outputs.map(output => [output.outputId, output]))
  return presentation.headlineOutputIds.map(id => byId.get(id)).filter((output): output is PresentationOutput => Boolean(output) && output!.key !== 'verdict' && output!.semanticType !== 'enum')
}

export function formatOutput(value: string | boolean | null | undefined, output: PresentationOutput | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  switch (output?.semanticType) {
    case 'money': case 'moneyPence': return output.semanticType === 'moneyPence' ? poundsExact(Number(value) / 100) : poundsExact(value)
    case 'ratio': case 'percentage': return percent(value, 1)
    case 'integer': case 'count': case 'months': case 'days': return Number(value).toLocaleString('en-GB')
    case 'decimal': case 'areaSqm': return Number(value).toLocaleString('en-GB', { maximumFractionDigits: 2 })
    case 'enum': return humanize(String(value))
    default: return String(value)
  }
}

export interface ModelFigures {
  /** First headline figure (e.g. profit) and its label. */
  h1: string; h1Label: string
  /** Second headline figure (e.g. margin on GDV) and its label; '' when the model declares one only. */
  h2: string; h2Label: string
}

/** The two headline figures the model itself declares for this result. */
export function modelFigures(model: SelectedDealModel | undefined, result: CardDealResult | undefined): ModelFigures {
  if (result?.bid && (result.bid.state !== 'computed' || result.bid.maxBidPence == null)) result = undefined
  const [first, second] = headlineOutputs(model)
  const read = (output: PresentationOutput | undefined) => output ? formatOutput(result?.outputs[output.key], output) : '—'
  // Older responses carry no presentation: fall back to the Flip-style keys the first card used.
  if (!first) return { h1: poundsExact(result?.outputs.profit as string | undefined), h1Label: 'Profit', h2: percent(result?.outputs.marginOnGdv as string | undefined, 1), h2Label: 'Margin on GDV' }
  return { h1: read(first), h1Label: first.label, h2: second ? read(second) : '', h2Label: second?.label ?? '' }
}

/**
 * Whether a card's saved result is not the latest calculation: it carries
 * stale reasons, or its freshness is `updating`. Cards are parsed loosely, so
 * both fields are read defensively; an older server sends no reasons.
 */
export function staleOf(result: CardDealResult | undefined): { stale: boolean; reasons: string[] } {
  if (!result) return { stale: false, reasons: [] }
  const loose = result as { staleReasons?: unknown; freshness?: unknown }
  const reasons = parseStaleReasons(loose.staleReasons)
  return { stale: reasons.length > 0 || loose.freshness === 'updating', reasons }
}

export function maxBidPence(result: CardDealResult | undefined): number | null {
  return result?.bid?.state === 'computed' && result.bid.maxBidPence != null && result.state === 'computed' ? Number(result.bid.maxBidPence) : null
}

/** Width of the bid bar: max bid as a share of the marketed price, clamped to the track. */
export function bidFraction(maxBid: number | null, guide: number | null): number | null {
  if (maxBid === null || !guide) return null
  return Math.min(100, Math.max(2, (maxBid / guide) * 100))
}
